import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Dealer Dashboard
 *
 * Central hub for dealers to manage their business operations
 * - Overview statistics (memos, orders)
 * - Recent activity feed
 * - Quick actions
 * - Alerts and notifications
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { apiClient } from '../../utils/apiClient';
import { MemoStatus } from '../../types/memo';
import { MemoHelpers } from '../../utils/memoHelpers';
import { PageContainer, PageTitle, SectionTitle, Text } from '../../components/PageStyles';
import styled from 'styled-components';
import { FaGem, FaClipboardList, FaShoppingCart, FaUser } from 'react-icons/fa';
// Styled Components
const DashboardGrid = styled.div `
  display: grid;
  gap: 24px;
  margin-bottom: 32px;
`;
const StatsGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;
const StatCard = styled.div `
  background: var(--bg-secondary);
  padding: 24px;
  border: 2px solid ${props => {
    switch (props.$variant) {
        case 'warning': return '#f59e0b';
        case 'success': return 'var(--success-color)';
        case 'mock': return '#6366f1';
        default: return 'var(--border-color)';
    }
}};
  position: relative;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-color: ${props => {
    switch (props.$variant) {
        case 'warning': return '#f59e0b';
        case 'success': return 'var(--success-color)';
        case 'mock': return '#6366f1';
        default: return 'var(--border-color-hover)';
    }
}};
  }

  .stat-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 12px;
  }

  .stat-label {
    font-size: 11px;
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: 600;
  }

  .stat-value {
    font-size: 36px;
    font-weight: bold;
    color: var(--text-primary);
    margin: 8px 0;
    line-height: 1;
  }

  .stat-sublabel {
    font-size: 13px;
    color: var(--text-tertiary);
    margin-top: 4px;
  }

  .stat-trend {
    font-size: 12px;
    margin-top: 8px;
    display: flex;
    align-items: center;
    gap: 4px;

    &.positive {
      color: var(--success-color);
    }

    &.negative {
      color: var(--error-color);
    }
  }
`;
const MockBadge = styled.div `
  background: #6366f1;
  color: white;
  font-size: 9px;
  padding: 3px 8px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 2px;
`;
const Section = styled.div `
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  padding: 24px;
  margin-bottom: 24px;
`;
const QuickActionsGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-top: 16px;
`;
const QuickActionButton = styled.button `
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  color: var(--text-primary);
  display: flex;
  flex-direction: column;
  gap: 12px;

  &:hover {
    border-color: var(--border-color-hover);
    background: var(--bg-secondary);
  }

  .action-icon {
    font-size: 20px;
    color: var(--text-secondary);
  }

  .action-title {
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: var(--text-primary);
  }

  .action-description {
    font-size: 12px;
    color: var(--text-tertiary);
    line-height: 1.5;
  }
`;
const ActivityTable = styled.div `
  margin-top: 16px;
  border: 1px solid var(--border-color);
`;
const ActivityRow = styled.div `
  display: grid;
  grid-template-columns: 1fr auto 120px 100px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-color);
  align-items: center;
  gap: 16px;
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--bg-tertiary);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .activity-content {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .activity-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-primary);
    letter-spacing: 0.3px;
  }

  .activity-description {
    font-size: 12px;
    color: var(--text-tertiary);
    line-height: 1.4;
  }

  .activity-time {
    font-size: 11px;
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 600;
    text-align: right;

    @media (max-width: 768px) {
      text-align: left;
    }
  }

  .activity-status {
    text-align: right;

    @media (max-width: 768px) {
      text-align: left;
    }
  }

  .activity-badge {
    display: inline-block;
    font-size: 10px;
    padding: 4px 10px;
    background: var(--bg-primary);
    border: 1px solid var(--border-color);
    color: var(--text-secondary);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;
const AlertBox = styled.div `
  background: var(--bg-secondary);
  border: 1px solid ${props => {
    switch (props.$type) {
        case 'warning': return '#f59e0b';
        case 'success': return '#22c55e';
        default: return '#6366f1';
    }
}};
  padding: 16px 20px;
  margin-bottom: 12px;

  .alert-title {
    font-size: 12px;
    font-weight: 700;
    color: var(--text-primary);
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  .alert-message {
    font-size: 13px;
    color: var(--text-secondary);
    line-height: 1.5;
  }
`;
const EmptyState = styled.div `
  text-align: center;
  padding: 48px 24px;
  color: var(--text-tertiary);

  .empty-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-secondary);
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  .empty-description {
    font-size: 13px;
    color: var(--text-tertiary);
  }
`;
const DealerDashboard = () => {
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const [memos, setMemos] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    useEffect(() => {
        loadDashboardData();
    }, []);
    const loadDashboardData = async () => {
        try {
            setIsLoading(true);
            // Load real memo data
            const response = await apiClient.get('memos/my-requests');
            if (response.ok && response.data?.success) {
                setMemos(response.data.memos);
            }
        }
        catch (error) {
            console.error('Failed to load dashboard data:', error);
        }
        finally {
            setIsLoading(false);
        }
    };
    // Calculate real memo statistics
    const memoStats = MemoHelpers.calculateMemoStatistics(memos);
    const activeMemos = memos.filter(m => [MemoStatus.APPROVED, MemoStatus.SHIPPED, MemoStatus.WITH_CLIENT].includes(m.status));
    const recentMemos = [...memos]
        .sort((a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime())
        .slice(0, 5);
    // MOCK DATA - Orders (Feature in development)
    const mockOrderStats = {
        total: 12,
        pending: 3,
        completed: 9,
        totalValue: 487500
    };
    // MOCK DATA - Recent Activity (mixed with real memo data)
    const mockRecentActivity = [
        ...recentMemos.slice(0, 3).map(memo => ({
            type: 'memo',
            title: `Memo Request - ${memo.diamondDetails.caratWeight}ct ${memo.diamondDetails.shape}`,
            description: `${memo.diamondDetails.color} / ${memo.diamondDetails.clarity} / ${memo.diamondDetails.cut}`,
            time: new Date(memo.requestedAt).toLocaleDateString(),
            status: memo.status,
            isMock: false
        })),
        {
            type: 'order',
            title: 'Order #1245 Completed',
            description: '2.5ct Round Diamond delivered',
            time: '2 days ago',
            status: 'completed',
            isMock: true
        }
    ];
    if (isLoading) {
        return (_jsx(PageContainer, { children: _jsx(PageTitle, { children: "Loading Dashboard..." }) }));
    }
    return (_jsxs(PageContainer, { children: [_jsx(PageTitle, { children: "Dealer Dashboard" }), _jsxs(Text, { style: { marginBottom: '32px' }, children: ["Welcome back, ", user?.profile?.firstName || user?.email?.split('@')[0] || 'Dealer', "! Here's your business overview."] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "Alerts & Notifications" }), memoStats.overdueCount > 0 && (_jsxs(AlertBox, { "$type": "warning", children: [_jsx("div", { className: "alert-title", children: "Overdue Memos" }), _jsxs("div", { className: "alert-message", children: ["You have ", memoStats.overdueCount, " overdue memo", memoStats.overdueCount > 1 ? 's' : '', ". Please return or purchase soon."] })] })), _jsxs(AlertBox, { "$type": "info", children: [_jsx("div", { className: "alert-title", children: "System Notice" }), _jsx("div", { className: "alert-message", children: "Some features on this dashboard show mock data (marked with MOCK badge). These features are currently in development." })] })] }), _jsxs(StatsGrid, { children: [_jsxs(StatCard, { children: [_jsx("div", { className: "stat-header", children: _jsx("div", { className: "stat-label", children: "Active Memos" }) }), _jsx("div", { className: "stat-value", children: activeMemos.length }), _jsxs("div", { className: "stat-sublabel", children: ["Total Value: $", (memoStats.totalValue / 1000).toFixed(0), "K"] }), memoStats.overdueCount > 0 && (_jsxs("div", { className: "stat-trend negative", children: ["\u26A0\uFE0F ", memoStats.overdueCount, " overdue"] }))] }), _jsxs(StatCard, { "$variant": "mock", children: [_jsxs("div", { className: "stat-header", children: [_jsx("div", { className: "stat-label", children: "Orders" }), _jsx(MockBadge, { children: "MOCK" })] }), _jsx("div", { className: "stat-value", children: mockOrderStats.total }), _jsxs("div", { className: "stat-sublabel", children: [mockOrderStats.pending, " pending \u2022 ", mockOrderStats.completed, " completed"] }), _jsxs("div", { className: "stat-trend positive", children: ["\u2191 $", (mockOrderStats.totalValue / 1000).toFixed(0), "K total value"] })] }), _jsxs(StatCard, { "$variant": "warning", children: [_jsxs("div", { className: "stat-header", children: [_jsx("div", { className: "stat-label", children: "Pending Actions" }), _jsx(MockBadge, { children: "MOCK" })] }), _jsx("div", { className: "stat-value", children: memoStats.byStatus[MemoStatus.PENDING] + mockOrderStats.pending }), _jsxs("div", { className: "stat-sublabel", children: [memoStats.byStatus[MemoStatus.PENDING], " memos \u2022 ", mockOrderStats.pending, " orders"] })] })] }), _jsxs(DashboardGrid, { children: [_jsxs(Section, { children: [_jsx(SectionTitle, { children: "Quick Actions" }), _jsxs(QuickActionsGrid, { children: [_jsxs(QuickActionButton, { onClick: () => navigate('/atelier'), children: [_jsx(FaGem, { className: "action-icon" }), _jsx("div", { className: "action-title", children: "Browse Inventory" }), _jsx("div", { className: "action-description", children: "Search and view available diamonds" })] }), _jsxs(QuickActionButton, { onClick: () => navigate('/memos'), children: [_jsx(FaClipboardList, { className: "action-icon" }), _jsx("div", { className: "action-title", children: "Manage Memos" }), _jsx("div", { className: "action-description", children: "Track your memo requests" })] }), _jsxs(QuickActionButton, { onClick: () => navigate('/atelier'), children: [_jsx(FaShoppingCart, { className: "action-icon" }), _jsxs("div", { className: "action-title", children: ["Place Order ", _jsx(MockBadge, { style: { display: 'inline', marginLeft: '8px' }, children: "MOCK" })] }), _jsx("div", { className: "action-description", children: "Purchase diamonds" })] }), _jsxs(QuickActionButton, { onClick: () => navigate('/profile'), children: [_jsx(FaUser, { className: "action-icon" }), _jsx("div", { className: "action-title", children: "View Profile" }), _jsx("div", { className: "action-description", children: "Update your information" })] })] })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "Recent Activity" }), _jsx(ActivityTable, { children: mockRecentActivity.length === 0 ? (_jsxs(EmptyState, { children: [_jsx("div", { className: "empty-title", children: "No Recent Activity" }), _jsx("div", { className: "empty-description", children: "Your recent transactions and updates will appear here" })] })) : (mockRecentActivity.map((activity, index) => (_jsxs(ActivityRow, { children: [_jsxs("div", { className: "activity-content", children: [_jsx("div", { className: "activity-title", children: activity.title }), _jsx("div", { className: "activity-description", children: activity.description })] }), activity.isMock && (_jsx(MockBadge, { children: "MOCK" })), _jsx("div", { className: "activity-time", children: activity.time }), _jsx("div", { className: "activity-status", children: _jsx("span", { className: "activity-badge", children: typeof activity.status === 'string' ? activity.status.toUpperCase() : MemoHelpers.getMemoStatusLabel(activity.status) }) })] }, index)))) })] }), _jsxs(Section, { children: [_jsxs(SectionTitle, { children: ["Account Summary", _jsx(MockBadge, { style: { marginLeft: '12px' }, children: "PARTIALLY MOCK" })] }), _jsxs(ActivityTable, { children: [_jsxs(ActivityRow, { children: [_jsxs("div", { className: "activity-content", children: [_jsx("div", { className: "activity-title", children: "Total Memos" }), _jsxs("div", { className: "activity-description", children: ["Active: ", activeMemos.length, " \u2022 Pending: ", memoStats.byStatus[MemoStatus.PENDING], " \u2022 Completed: ", memoStats.byStatus[MemoStatus.RETURNED] + memoStats.byStatus[MemoStatus.SOLD]] })] }), _jsx("div", {}), _jsx("div", { className: "activity-time", children: "REAL DATA" }), _jsx("div", { className: "activity-status", children: _jsxs("span", { className: "activity-badge", children: [memos.length, " TOTAL"] }) })] }), _jsxs(ActivityRow, { children: [_jsxs("div", { className: "activity-content", children: [_jsx("div", { className: "activity-title", children: "Orders Placed" }), _jsx("div", { className: "activity-description", children: "Lifetime orders and purchase history" })] }), _jsx(MockBadge, { children: "MOCK" }), _jsx("div", { className: "activity-time", children: "LIFETIME" }), _jsx("div", { className: "activity-status", children: _jsxs("span", { className: "activity-badge", children: [mockOrderStats.total, " ORDERS"] }) })] })] })] })] })] }));
};
export default DealerDashboard;
